// src/components/design/TemplateDesignPanel.jsx
// Panneau "Design" simplifié :
//   1. Choisir un template (10 sélectionnés, "Voir tous" pour les autres)
//   2. Personnaliser en 3 réglages : couleur principale, police, forme des boutons
//   3. Réglages avancés repliés par défaut (tout l'ancien panneau)
//
//   <TemplateDesignPanel
//     settings={settings}
//     onChange={(patch) => setSettings((s) => ({ ...s, ...patch }))}
//   />
import { useEffect, useState } from 'react'
import { RotateCcw, ChevronDown } from 'lucide-react'
import {
  TEMPLATES, getTemplatePatch, getTheme, makeBgStyle, loadGoogleFonts, FONTS,
  isLightColor, BUTTON_SHAPES,
  SIZE_LIMITS, SIZE_PRESETS, LAYOUT_DEFAULT_AVATAR, LAYOUT_DEFAULT_HEADER,
  HEADER_LAYOUTS, LINK_LAYOUTS, LINK_ICON_STYLES, SUBTITLE_MODES, SOCIAL_LAYOUTS,
  SOCIAL_STYLES, SOCIAL_POSITIONS, ORNAMENTS, PAGE_FRAMES, HERO_ALIGNS, HEADLINE_POS,
  DEFAULT_SETTINGS,
} from '../../lib/pageDesign'

/* Les templates mis en avant (les plus soignés, faciles à vendre) */
const FEATURED_IDS = [
  'pro-navy', 'pro-white', 'creator-gradient', 'creator-hero', 'blush-style',
  'beige-ornate', 'rose-gold', 'music-dark', 'shop-warm', 'neon-dev',
]
const FEATURED = FEATURED_IDS.map((id) => TEMPLATES.find((t) => t.id === id)).filter(Boolean)

/* Polices simples à proposer au client */
const SIMPLE_FONTS = ['inter', 'poppins', 'montserrat', 'jakarta', 'playfair', 'cormorant', 'spacegrotesk']
  .map((id) => FONTS.find((f) => f.id === id))
  .filter(Boolean)

const COLOR_SWATCHES = [
  '#14B8A6', '#2563eb', '#7c3aed', '#ec4899', '#ef4444', '#f97316', '#eab308', '#16a34a', '#111111', '#ffffff',
]

const field = 'w-full px-3 py-2 bg-slate-50 rounded-lg text-sm border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20'
const Title = ({ children }) => (
  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider pt-4 pb-2">{children}</h3>
)

const isHex = (v) => /^#[0-9a-f]{6}$/i.test(v || '')

/* Applique une couleur principale en restant cohérent avec le template */
function mainColorPatch(s, color) {
  const txt = isLightColor(color) ? '#111111' : '#ffffff'
  const patch = { customColors: true, customAccent: color }
  if (isHex(s.avatarRing)) patch.avatarRing = color
  if (s.highlightFirst) {
    patch.linkHighlightBg = color
    patch.linkHighlightText = txt
  } else if (s.linkBg === 'transparent') {
    patch.linkBorder = color
    patch.linkText = color
  } else if (isHex(s.linkBg)) {
    patch.linkBg = color
    patch.linkText = txt
  }
  return patch
}

function Select({ label, value, options, onChange }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-navy mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={field}>
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </div>
  )
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#006b5f]" />
      <span className="text-xs font-semibold text-navy">{label}</span>
    </label>
  )
}

function TextField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-navy mb-1">{label}</label>
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={field} />
    </div>
  )
}

/* Couleur : sélecteur + saisie libre (accepte aussi un dégradé CSS ou "transparent") */
function ColorRow({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-navy mb-1">{label}</label>
      <div className="flex gap-2">
        <input type="color" value={isHex(value) ? value : '#888888'} onChange={(e) => onChange(e.target.value)}
          className="w-10 h-9 rounded-lg border border-slate-200 cursor-pointer shrink-0" />
        <input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="auto"
          className="flex-1 min-w-0 px-2 py-2 bg-slate-50 rounded-lg text-xs border border-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-[#006b5f]/20" />
      </div>
    </div>
  )
}

/* Curseur de taille ; "Auto" (0) pour les tailles qui ont une valeur par défaut selon le layout */
function SizeSlider({ k, value, effectiveAuto, onChange }) {
  const lim = SIZE_LIMITS[k]
  const isAuto = lim.auto && !(Number(value) > 0)
  const shown = isAuto ? effectiveAuto || lim.min : Number(value)
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[11px] font-semibold text-navy">{lim.label}</label>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-mono text-slate-500">
            {isAuto ? 'Auto' : `${shown}${lim.unit}`}
          </span>
          {lim.auto && !isAuto && (
            <button type="button" onClick={() => onChange(0)} className="text-[10px] font-semibold text-[#006b5f] hover:underline">Auto</button>
          )}
        </div>
      </div>
      <input type="range" min={lim.min} max={lim.max} step={lim.step} value={shown}
        onChange={(e) => onChange(Number(e.target.value))} className="w-full accent-[#006b5f]" />
    </div>
  )
}

/* Miniature d'un template (mêmes couleurs/formes que le rendu réel) */
function TemplateThumb({ tpl, active, onClick }) {
  const s = { ...DEFAULT_SETTINGS, ...tpl.settings }
  const theme = getTheme(s)
  const layout = Number(s.headerLayout)
  const link = s.linkBg || theme.accent
  const linkTxt = s.linkText || theme.text
  const radius = s.linkLayout === 'card' ? 4 : s.buttonShape === 'sharp' ? 0 : s.buttonShape === 'square' ? 3 : s.buttonShape === 'rounded' ? 5 : 99
  const avatar = (
    <div style={{
      width: 22, height: layout === 6 ? 26 : 22, margin: '0 auto',
      borderRadius: layout === 6 ? '11px 11px 4px 4px' : s.avatarShape === 'square' ? 2 : '50%',
      background: s.avatarRing || 'rgba(255,255,255,.55)', border: s.avatarRing ? 'none' : `1px solid ${theme.text}55`,
    }} />
  )
  return (
    <button type="button" onClick={onClick}
      className={`text-left rounded-xl p-1.5 border-2 transition ${active ? 'border-[#006b5f] bg-[#006b5f]/5' : 'border-transparent hover:border-slate-200'}`}>
      <div className="rounded-lg overflow-hidden relative" style={{ ...makeBgStyle(theme, s), height: 112 }}>
        {layout === 5 && <div style={{ height: 48, background: `linear-gradient(${theme.accent}66, ${theme.bg})` }} />}
        {layout === 9 && <div style={{ height: 30, background: s.headerBg || theme.accent }} />}
        <div style={{ padding: '6px 8px', marginTop: layout === 5 ? -14 : 0 }}>
          {layout !== 5 && avatar}
          <div style={{ height: 3, width: '45%', margin: '4px auto 5px', background: theme.text, opacity: 0.8, borderRadius: 2 }} />
          {[0, 1, 2].map((i) => (
            <div key={i} style={{
              height: s.linkLayout === 'card' || s.linkLayout === 'card-round' ? 11 : 8,
              margin: '3px auto', width: `${Math.min(100, s.linkWidth || 100) - 6}%`,
              borderRadius: radius, background: i === 0 && s.highlightFirst && s.linkHighlightBg ? s.linkHighlightBg : link,
              border: s.linkBorder ? `1px solid ${s.linkBorder}` : 'none', color: linkTxt,
            }} />
          ))}
        </div>
      </div>
      <p className="text-[11px] font-semibold text-navy mt-1.5 leading-tight">{tpl.name}</p>
      <p className="text-[10px] text-slate-400">{tpl.tag}</p>
    </button>
  )
}

export default function TemplateDesignPanel({ settings, onChange }) {
  const s = { ...DEFAULT_SETTINGS, ...settings }
  const set = (k, v) => onChange({ [k]: v })
  const layout = Number(s.headerLayout) || 1
  const [showAll, setShowAll] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const currentTpl = TEMPLATES.find((t) => t.id === s.template)
  const shown = showAll ? TEMPLATES : FEATURED
  const mainColor = isHex(s.customAccent) ? s.customAccent : '#14B8A6'

  // charge les polices des templates affichés + celles en cours (un seul <link>, donc un seul effet)
  useEffect(() => {
    loadGoogleFonts([
      ...TEMPLATES.flatMap((t) => [t.settings.font, t.settings.nameFont, t.settings.headlineFont]),
      ...SIMPLE_FONTS.map((f) => f.id),
      s.font, s.nameFont, s.headlineFont,
    ])
  }, [s.font, s.nameFont, s.headlineFont])

  const resetSizes = () =>
    onChange({ ...SIZE_PRESETS.find((p) => p.id === 'normal').patch, headerHeight: 0, avatarPx: 0 })

  return (
    <div>
      {/* ---------- 1. CHOISIR ---------- */}
      <Title>1 · Choisissez un template</Title>
      <div className="grid grid-cols-2 gap-2 max-h-[520px] overflow-y-auto pr-1">
        {shown.map((tpl) => (
          <TemplateThumb key={tpl.id} tpl={tpl} active={s.template === tpl.id}
            onClick={() => onChange(getTemplatePatch(settings, tpl))} />
        ))}
      </div>
      <button type="button" onClick={() => setShowAll((v) => !v)}
        className="mt-2 w-full py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-navy">
        {showAll ? 'Voir les templates recommandés' : `Voir tous les templates (${TEMPLATES.length})`}
      </button>
      <p className="text-[10px] text-slate-400 mt-2">
        Votre contenu (nom, bio, liens, photos) n'est jamais remplacé. L'aperçu de la page se met à jour à chaque clic.
      </p>

      {/* ---------- 2. PERSONNALISER ---------- */}
      <Title>2 · Personnalisez en 3 réglages</Title>
      <div className="space-y-4">
        <div>
          <label className="block text-[11px] font-semibold text-navy mb-1.5">Couleur principale</label>
          <div className="flex flex-wrap items-center gap-2">
            {COLOR_SWATCHES.map((c) => (
              <button key={c} type="button" title={c} onClick={() => onChange(mainColorPatch(s, c))}
                className={`w-7 h-7 rounded-full border-2 transition ${mainColor.toLowerCase() === c ? 'border-[#006b5f] scale-110' : 'border-slate-200'}`}
                style={{ background: c }} />
            ))}
            <input type="color" value={mainColor} onChange={(e) => onChange(mainColorPatch(s, e.target.value))}
              title="Autre couleur" className="w-8 h-8 rounded-lg border border-slate-200 cursor-pointer" />
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-navy mb-1.5">Police</label>
          <div className="grid grid-cols-2 gap-1.5">
            {SIMPLE_FONTS.map((f) => (
              <button key={f.id} type="button" onClick={() => onChange({ font: f.id, nameFont: '' })}
                className={`px-3 py-2 rounded-lg text-sm border-2 transition ${s.font === f.id && !s.nameFont ? 'border-[#006b5f] bg-[#006b5f]/5' : 'border-slate-200 hover:border-slate-300'}`}
                style={{ fontFamily: f.family }}>
                {f.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-navy mb-1.5">Forme des boutons</label>
          <div className="flex gap-1.5">
            {BUTTON_SHAPES.map((b) => (
              <button key={b.id} type="button" onClick={() => set('buttonShape', b.id)}
                className={`flex-1 py-2 text-[11px] font-semibold border-2 transition ${s.buttonShape === b.id ? 'border-[#006b5f] bg-[#006b5f]/5 text-[#006b5f]' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                style={{ borderRadius: b.radius === '999px' ? 999 : Math.min(parseInt(b.radius, 10), 14) }}>
                {b.name}
              </button>
            ))}
          </div>
        </div>

        {currentTpl && (
          <button type="button" onClick={() => onChange(getTemplatePatch(settings, currentTpl))}
            className="w-full py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-navy inline-flex items-center justify-center gap-1.5">
            <RotateCcw className="w-3.5 h-3.5" /> Revenir au template d'origine
          </button>
        )}
      </div>

      {/* ---------- 3. AVANCÉ (replié) ---------- */}
      <button type="button" onClick={() => setShowAdvanced((v) => !v)}
        className="mt-5 w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider hover:bg-slate-50">
        Réglages avancés
        <ChevronDown className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
      </button>

      {showAdvanced && (
        <div>
          <Title>Dimensions</Title>
          <div className="flex gap-1.5 mb-3">
            {SIZE_PRESETS.map((p) => (
              <button key={p.id} type="button" onClick={() => onChange(p.patch)}
                className="flex-1 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-[11px] font-semibold text-navy">{p.name}</button>
            ))}
            <button type="button" onClick={resetSizes} title="Tout remettre en automatique"
              className="px-2.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500"><RotateCcw className="w-3.5 h-3.5" /></button>
          </div>
          <div className="space-y-3">
            <SizeSlider k="contentWidth" value={s.contentWidth} onChange={(v) => set('contentWidth', v)} />
            <SizeSlider k="headerHeight" value={s.headerHeight} effectiveAuto={LAYOUT_DEFAULT_HEADER[layout]}
              onChange={(v) => set('headerHeight', v)} />
            <SizeSlider k="avatarPx" value={s.avatarPx} effectiveAuto={LAYOUT_DEFAULT_AVATAR[layout]}
              onChange={(v) => set('avatarPx', v)} />
            <SizeSlider k="linkWidth" value={s.linkWidth} onChange={(v) => set('linkWidth', v)} />
            <SizeSlider k="linkHeight" value={s.linkHeight} effectiveAuto={44} onChange={(v) => set('linkHeight', v)} />
            <SizeSlider k="linkGap" value={s.linkGap} onChange={(v) => set('linkGap', v)} />
          </div>

          <Title>En-tête</Title>
          <div className="space-y-3">
            <Select label="Disposition" value={layout} options={HEADER_LAYOUTS}
              onChange={(v) => set('headerLayout', Number(v))} />
            {layout === 5 && <Select label="Position du texte sur la photo" value={s.heroAlign} options={HERO_ALIGNS} onChange={(v) => set('heroAlign', v)} />}
            {layout === 9 && <ColorRow label="Couleur du bandeau" value={s.headerBg} onChange={(v) => set('headerBg', v)} />}
            <Select label="Cadre autour de la page" value={s.pageFrame} options={PAGE_FRAMES} onChange={(v) => set('pageFrame', v)} />
            <div className="grid grid-cols-2 gap-3">
              <ColorRow label="Anneau photo" value={s.avatarRing} onChange={(v) => set('avatarRing', v)} />
              <div>
                <label className="block text-[11px] font-semibold text-navy mb-1">Épaisseur anneau</label>
                <input type="number" min="0" max="12" value={s.avatarRingWidth || 0}
                  onChange={(e) => set('avatarRingWidth', Number(e.target.value))} className={field} />
              </div>
            </div>
            <TextField label="Accroche / profession" value={s.tagline} placeholder="Ex : Beauty designer"
              onChange={(v) => set('tagline', v)} />
            <div className="grid grid-cols-2 gap-3">
              <Toggle label="Accroche en MAJUSCULES" checked={s.taglineUppercase} onChange={(v) => set('taglineUppercase', v)} />
              <Toggle label="Nom en MAJUSCULES" checked={s.nameUppercase} onChange={(v) => set('nameUppercase', v)} />
              <Toggle label="Trait sous le nom" checked={s.nameUnderline} onChange={(v) => set('nameUnderline', v)} />
              <Toggle label="Badge vérifié" checked={s.verifiedBadge} onChange={(v) => set('verifiedBadge', v)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-navy mb-1">Taille du nom (px, 0 = auto)</label>
                <input type="number" min="0" max="64" value={s.nameSize || 0}
                  onChange={(e) => set('nameSize', Number(e.target.value))} className={field} />
              </div>
              <Select label="Police du nom" value={s.nameFont || ''} onChange={(v) => set('nameFont', v)}
                options={[{ id: '', name: 'Comme le texte' }, ...FONTS.map((f) => ({ id: f.id, name: f.name }))]} />
            </div>
          </div>

          <Title>Liens</Title>
          <div className="space-y-3">
            <Select label="Style des liens" value={s.linkLayout} options={LINK_LAYOUTS} onChange={(v) => set('linkLayout', v)} />
            {(s.linkLayout === 'card' || s.linkLayout === 'card-round') && (
              <div className="grid grid-cols-2 gap-3">
                <Select label="Icône" value={s.linkIconStyle} options={LINK_ICON_STYLES} onChange={(v) => set('linkIconStyle', v)} />
                <Select label="Sous-titre" value={s.linkShowSubtitle} options={SUBTITLE_MODES} onChange={(v) => set('linkShowSubtitle', v)} />
              </div>
            )}
            {s.linkLayout === 'ornate' && (
              <Select label="Ornement" value={s.ornament} options={ORNAMENTS} onChange={(v) => set('ornament', v)} />
            )}
            <div className="grid grid-cols-2 gap-3">
              <ColorRow label="Fond des liens" value={s.linkBg} onChange={(v) => set('linkBg', v)} />
              <ColorRow label="Texte des liens" value={s.linkText} onChange={(v) => set('linkText', v)} />
              <ColorRow label="Bordure des liens" value={s.linkBorder} onChange={(v) => set('linkBorder', v)} />
            </div>
            <p className="text-[10px] text-slate-400 -mt-1">« Fond » accepte une couleur, « transparent » ou un dégradé CSS. Vide = style de bouton classique.</p>
            <div className="grid grid-cols-2 gap-3">
              <Toggle label="Textes en MAJUSCULES" checked={s.linkUppercase} onChange={(v) => set('linkUppercase', v)} />
              <Toggle label="Mettre le 1er lien en avant" checked={s.highlightFirst} onChange={(v) => set('highlightFirst', v)} />
            </div>
            {s.highlightFirst && (
              <div className="grid grid-cols-2 gap-3">
                <ColorRow label="Fond du 1er lien" value={s.linkHighlightBg} onChange={(v) => set('linkHighlightBg', v)} />
                <ColorRow label="Texte du 1er lien" value={s.linkHighlightText} onChange={(v) => set('linkHighlightText', v)} />
              </div>
            )}
          </div>

          <Title>Réseaux sociaux</Title>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Select label="Affichage" value={s.socialLayout} options={SOCIAL_LAYOUTS} onChange={(v) => set('socialLayout', v)} />
              <Select label="Position" value={s.socialPosition} options={SOCIAL_POSITIONS} onChange={(v) => set('socialPosition', v)} />
            </div>
            {s.socialLayout === 'icons' && (
              <>
                <Select label="Style des icônes" value={s.socialStyle} options={SOCIAL_STYLES} onChange={(v) => set('socialStyle', v)} />
                {s.socialStyle === 'round-solid' && (
                  <div className="grid grid-cols-2 gap-3">
                    <ColorRow label="Fond icône" value={s.socialBg} onChange={(v) => set('socialBg', v)} />
                    <ColorRow label="Couleur icône" value={s.socialFg} onChange={(v) => set('socialFg', v)} />
                  </div>
                )}
                <Toggle label="Traits décoratifs de chaque côté" checked={s.socialLines} onChange={(v) => set('socialLines', v)} />
              </>
            )}
          </div>

          <Title>Textes décoratifs</Title>
          <div className="space-y-3">
            <TextField label="Titre manuscrit" value={s.headline} placeholder="Ex : let's be friends!"
              onChange={(v) => set('headline', v)} />
            {s.headline && (
              <div className="grid grid-cols-2 gap-3">
                <Select label="Position" value={s.headlinePos} options={HEADLINE_POS} onChange={(v) => set('headlinePos', v)} />
                <Select label="Police" value={s.headlineFont || ''} onChange={(v) => set('headlineFont', v)}
                  options={[{ id: '', name: 'Comme le texte' }, ...FONTS.map((f) => ({ id: f.id, name: f.name }))]} />
              </div>
            )}
            <TextField label="Texte de pied de page" value={s.footerText} placeholder="© 2026 Votre nom. Tous droits réservés."
              onChange={(v) => set('footerText', v)} />
          </div>
        </div>
      )}
    </div>
  )
}